/**
 * useCategoryDragAndDrop Hook
 * 
 * Handles drag-and-drop reordering for categories and groups.
 */

import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";
import { api } from "../../../services/api";
import { isDemoMode } from "../../../utils/demoMode";

export interface UseCategoryDragAndDropParams {
  workspaceId: string;
  selectedTab: "expense" | "income";
  categories: TransactionCategory[];
  groups: TransactionCategoryGroup[];
  groupedCategories: Record<string, TransactionCategory[]>;
  setCategories: (categories: TransactionCategory[]) => void;
  setGroups: (groups: TransactionCategoryGroup[]) => void;
  setError: (error: string) => void;
}

export interface UseCategoryDragAndDropReturn {
  handleCategoryDragEnd: (event: DragEndEvent, groupId: string) => Promise<void>;
  handleGroupDragEnd: (event: DragEndEvent) => Promise<void>;
}

/**
 * Hook for category and group drag-and-drop handlers
 */
export function useCategoryDragAndDrop({
  workspaceId,
  selectedTab,
  categories,
  groups,
  groupedCategories,
  setCategories,
  setGroups,
  setError,
}: UseCategoryDragAndDropParams): UseCategoryDragAndDropReturn {
  // Handle drag end for categories (reorder categories within a group)
  const handleCategoryDragEnd = async (event: DragEndEvent, groupId: string): Promise<void> => {
    if (isDemoMode()) {
      return;
    }

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const groupCategories = groupedCategories[groupId] || [];
    const oldIndex = groupCategories.findIndex((cat) => cat.id === active.id);
    const newIndex = groupCategories.findIndex((cat) => cat.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder categories and update order field
    const reorderedCategories = arrayMove(groupCategories, oldIndex, newIndex);
    
    // Update local state immediately for better UX
    const updatedCategories = [...categories];
    reorderedCategories.forEach((reorderedCategory, index) => {
      const categoryIndex = updatedCategories.findIndex((cat) => cat.id === reorderedCategory.id);
      if (categoryIndex !== -1) {
        (updatedCategories[categoryIndex] as any).order = index;
      }
    });
    setCategories(updatedCategories);

    // TODO: Update order in backend API (need to add order field support)
    // For now, just update local state
  };

  // Handle drag end for groups (reorder groups)
  const handleGroupDragEnd = async (event: DragEndEvent): Promise<void> => {
    if (isDemoMode()) {
      return;
    }

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = groups.findIndex((g) => g.id === active.id);
    const newIndex = groups.findIndex((g) => g.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update local state immediately for better UX
    const newGroups = arrayMove(groups, oldIndex, newIndex) as TransactionCategoryGroup[];
    setGroups(newGroups);

    // Update order in backend API for all affected groups
    try {
      // Update order for all groups (using their new positions as order values)
      // Only update groups with valid IDs and names
      const updatePromises = newGroups
        .filter((group) => group.id && group.name)
        .map((group, index) => {
          return api.updateGroup(workspaceId, group.id, undefined, undefined, undefined, index);
        });
      
      await Promise.all(updatePromises);
    } catch (err: any) {
      console.error("Error updating group order:", err);
      // Revert local state on error
      setGroups(groups);
      setError(err.message || "Failed to update group order");
    }
  };

  return {
    handleCategoryDragEnd,
    handleGroupDragEnd,
  };
}
